import React, { FunctionComponent, PropsWithChildren, ReactNode } from 'react'
import { StatusBar, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import styled, { useTheme } from 'styled-components/native'

import { Helmet } from 'libs/react-helmet/Helmet'
import { useColorScheme } from 'libs/styled/useColorScheme'
import { RemoteIllustration, RemoteIllustrationProps } from 'ui/components/RemoteIllustration'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { Button } from 'ui/designSystem/Button/Button'
import { Page } from 'ui/pages/Page'
import { AccessibleIcon } from 'ui/svg/icons/types'
import { Typo } from 'ui/theme'
import { illustrationSizes } from 'ui/theme/illustrationSizes'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

type ButtonProps = {
  wording: string
  icon?: FunctionComponent<AccessibleIcon>
  disabled?: boolean
  isLoading?: boolean
  onPress: () => void
}

type Props = PropsWithChildren<{
  illustration: FunctionComponent<AccessibleIcon>
  title: string
  helmetTitle?: string
  header?: ReactNode
  subtitle?: string
  noIndex?: boolean
  buttonPrimary?: ButtonProps
  buttonTertiary?: ButtonProps
  buttonTertiaryExternalNav?: ReactNode
  remoteIllustration?: RemoteIllustrationProps
}>

// NEVER EVER USE NAVIGATION (OR ANYTHING FROM @react-navigation)
// ON THIS PAGE OR IT WILL BREAK!!!
// THE NAVIGATION CONTEXT IS NOT ALWAYS LOADED WHEN WE DISPLAY
// EX: ScreenErrorProvider IS OUTSIDE NAVIGATION!
export const GenericErrorPage: FunctionComponent<Props> = ({
  header,
  illustration: IllustrationComponent,
  title,
  subtitle,
  helmetTitle,
  noIndex = true,
  buttonPrimary,
  buttonTertiary,
  buttonTertiaryExternalNav,
  remoteIllustration,
  children,
}) => {
  const { top } = useSafeAreaInsets()
  const { designSystem } = useTheme()
  const colorScheme = useColorScheme()

  const renderIllustration = () => {
    if (remoteIllustration) return <RemoteIllustration {...remoteIllustration} />

    if (IllustrationComponent)
      return (
        <IllustrationComponent
          size={illustrationSizes.fullPage}
          color={designSystem.color.icon.brandPrimary}
        />
      )

    return null
  }

  return (
    <React.Fragment>
      {helmetTitle && !noIndex ? (
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
      ) : null}
      {noIndex && !helmetTitle ? (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      ) : null}
      <StatusBar
        barStyle={colorScheme === 'dark' ? 'light-content' : 'dark-content'}
        backgroundColor={
          colorScheme === 'dark'
            ? designSystem.color.background.lockedInverted
            : designSystem.color.background.locked
        }
      />
      <Page>
        {header}
        <Placeholder height={top} />
        <Container>
          <View>
            <IllustrationContainer>{renderIllustration()}</IllustrationContainer>
            <TextContainer gap={4}>
              <StyledTitle {...setTextSemantic('h1')}>{title}</StyledTitle>
              {subtitle ? (
                <StyledSubtitle {...setTextSemantic('h2')}>{subtitle}</StyledSubtitle>
              ) : null}
            </TextContainer>
            {children ? <ChildrenContainer>{children}</ChildrenContainer> : null}
            {buttonPrimary || buttonTertiary || buttonTertiaryExternalNav ? (
              <ButtonContainer gap={4}>
                {buttonPrimary?.onPress ? (
                  <Button
                    key={1}
                    wording={buttonPrimary.wording}
                    onPress={buttonPrimary.onPress}
                    isLoading={buttonPrimary.isLoading}
                    disabled={buttonPrimary.disabled}
                    icon={buttonPrimary.icon}
                    color="brand"
                    fullWidth
                  />
                ) : null}

                {buttonTertiary?.onPress ? (
                  <Button
                    key={2}
                    wording={buttonTertiary.wording}
                    onPress={buttonTertiary.onPress}
                    isLoading={buttonTertiary.isLoading}
                    disabled={buttonTertiary.disabled}
                    icon={buttonTertiary.icon}
                    variant="tertiary"
                    color="neutral"
                    fullWidth
                  />
                ) : null}

                {buttonTertiaryExternalNav}
              </ButtonContainer>
            ) : null}
          </View>
        </Container>
      </Page>
    </React.Fragment>
  )
}

const Container = styled.View(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  paddingHorizontal: theme.contentPage.marginHorizontal,
  paddingVertical: theme.contentPage.marginVertical,
  overflow: 'scroll',
}))

const Placeholder = styled.View<{ height: number }>(({ height }) => ({
  height,
}))

const IllustrationContainer = styled.View(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.designSystem.size.spacing.xl,
}))

const StyledTitle = styled(Typo.Title2)({
  textAlign: 'center',
})

const StyledSubtitle = styled(Typo.Body)({
  textAlign: 'center',
})

const TextContainer = styled(ViewGap)(({ theme }) => ({
  alignItems: 'center',
  marginBottom: theme.designSystem.size.spacing.xxl,
}))

const ChildrenContainer = styled.View(({ theme }) => ({
  marginBottom: theme.designSystem.size.spacing.xxl,
}))

const ButtonContainer = styled(ViewGap)({
  alignItems: 'center',
})
